import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Col, Row, Statistic, Button, Space, Select, Spin, Tag, Typography, Progress, List, Table } from 'antd';
import { 
  UserOutlined, 
  CarOutlined, 
  GlobalOutlined, 
  CalendarOutlined,
  ArrowLeftOutlined,
  EnvironmentOutlined,
  PieChartOutlined,
  BarChartOutlined,
  TeamOutlined,
  RiseOutlined
} from '@ant-design/icons';
import moment from 'moment';
import 'moment/locale/tr';
import './Total.css';

const { Title, Text } = Typography;
const { Option } = Select;

const normalizeCity = (city) => {
  if (!city) return '';
  return city
    .toLowerCase()
    .trim()
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ö/g, 'o')
    .replace(/i̇/g, 'i');
};

const Total = ({ token }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedCaravanType, setSelectedCaravanType] = useState('');
  const navigate = useNavigate();

  moment.locale('tr');

  // API'den veri çekme
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await fetch("https://wfh4zanzfa.execute-api.eu-central-1.amazonaws.com/api1/analytics", {
        method: 'GET',
        headers: {
          'Authorization': `Basic ${token || sessionStorage.getItem('token')}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        setData(result);
      } else {
        console.error('Veri çekilemedi');
      }
    } catch (error) {
      console.error('API Hatası:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Şehir seçenekleri için benzersiz liste oluştur:
const uniqueCities = data.reduce((acc, item) => {
  if (item.CITY) {
    const normalizedCity = normalizeCity(item.CITY);
    if (!acc.find(city => normalizeCity(city) === normalizedCity)) {
      acc.push(item.CITY); // İlk gelen orijinal adı sakla
    }
  }
  return acc;
}, []).sort();

  // Filtrelenmiş veri
// Filtrelenmiş veri - 59. satırı şöyle değiştir:
const filteredData = data.filter(item => {
  if (selectedCity && normalizeCity(item.CITY) !== normalizeCity(selectedCity)) return false;
  if (selectedCaravanType && item.CARAVAN_TYPE !== selectedCaravanType) return false;
  return true;
});


  // İstatistikler hesaplama
  const statistics = {
    totalUsers: filteredData.length,
    citiesCount: [...new Set(filteredData.map(item => normalizeCity(item.CITY)).filter(Boolean))].length,
    caravanTypes: filteredData.reduce((acc, item) => {
      if (item.CARAVAN_TYPE) {
        acc[item.CARAVAN_TYPE] = (acc[item.CARAVAN_TYPE] || 0) + 1;
      }
      return acc;
    }, {}),
    languages: filteredData.reduce((acc, item) => {
      if (item.USED_LANGUAGE) {
        acc[item.USED_LANGUAGE] = (acc[item.USED_LANGUAGE] || 0) + 1;
      }
      return acc;
    }, {}),
    averageCaravanYear: filteredData.reduce((sum, item) => {
      return sum + (parseInt(item.CARAVAN_YEAR) || 0);
    }, 0) / (filteredData.filter(item => item.CARAVAN_YEAR).length || 1),
    // 112-121. satırları şöyle değiştirin:
    newUsersThisMonth: filteredData.filter(item => {
      if (!item.USER_CREATED_DATE) return false;
      const created = moment.unix(item.USER_CREATED_DATE);
      return created.isAfter(moment().subtract(30, 'days'));
    }).length,
    newUsersThisWeek: filteredData.filter(item => {
      if (!item.USER_CREATED_DATE) return false;
      const created = moment.unix(item.USER_CREATED_DATE);
      return created.isAfter(moment().subtract(7, 'days'));
    }).length
  };



// Sonra cityGroups kodunu şöyle değiştir:
const cityGroups = filteredData.reduce((acc, item) => {
  if (item.CITY) {
    const normalizedCity = normalizeCity(item.CITY);
    
    if (!acc[normalizedCity]) {
      acc[normalizedCity] = {
        name: item.CITY, // İlk gelen orijinal adı sakla
        normalizedName: normalizedCity,
        count: 0,
        caravanTypes: {},
        languages: {},
        users: []
      };
    }
    
    acc[normalizedCity].count++;
    acc[normalizedCity].users.push(item);
    
    // Karavan tipi dağılımı
    if (item.CARAVAN_TYPE) {
      acc[normalizedCity].caravanTypes[item.CARAVAN_TYPE] = 
        (acc[normalizedCity].caravanTypes[item.CARAVAN_TYPE] || 0) + 1;
    }
    
    // Dil dağılımı
    if (item.USED_LANGUAGE) {
      acc[normalizedCity].languages[item.USED_LANGUAGE] = 
        (acc[normalizedCity].languages[item.USED_LANGUAGE] || 0) + 1;
    }
  }
  return acc;
}, {});

  // En popüler şehirler - detaylı tablo için
  const topCitiesDetailed = Object.values(cityGroups)
    .sort((a, b) => b.count - a.count)
    .map(city => ({
      name: city.name,
      count: city.count,
      percentage: ((city.count / statistics.totalUsers) * 100).toFixed(1),
      mostUsedCaravan: Object.entries(city.caravanTypes)
        .sort((a, b) => b[1] - a[1])[0]?.[0] || 'Belirtilmemiş',
      mostUsedLanguage: Object.entries(city.languages)
        .sort((a, b) => b[1] - a[1])[0]?.[0] || 'Belirtilmemiş',
      users: city.users
    }));

  // En popüler şehirler - basit liste için
  const topCities = topCitiesDetailed.slice(0, 10);

  // Karavan model dağılımı
  const modelDistribution = filteredData.reduce((acc, item) => {
    if (item.CARAVAN_MODEL) {
      const model = item.CARAVAN_MODEL.substring(0, 30);
      acc[model] = (acc[model] || 0) + 1;
    }
    return acc;
  }, {});

  const topModels = Object.entries(modelDistribution)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  // Yıl bazında karavan dağılımı
  const yearDistribution = filteredData.reduce((acc, item) => {
    if (item.CARAVAN_YEAR) {
      const yearRange = Math.floor(parseInt(item.CARAVAN_YEAR) / 5) * 5;
      const range = `${yearRange}-${yearRange + 4}`;
      acc[range] = (acc[range] || 0) + 1;
    }
    return acc;
  }, {});

  // Plaka dağılımı
  const plateDistribution = filteredData.reduce((acc, item) => {
    if (item.PLATE_NUMBER) {
      const plateCode = item.PLATE_NUMBER.substring(0, 2);
      if (!isNaN(plateCode)) {
        acc[plateCode] = (acc[plateCode] || 0) + 1;
      }
    }
    return acc;
  }, {});

  const topPlates = Object.entries(plateDistribution)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5);

  // Şehirler için genişletilebilir tablo
  const expandedRowRender = (record) => {
    const cityUsers = record.users;
    const columns = [
      { 
        title: 'Ad Soyad', 
        key: 'name',
        render: (user) => `${user.NAME || ''} ${user.SURNAME || ''}`.trim() || 'Belirtilmemiş'
      },
      { title: 'E-posta', dataIndex: 'USER_ID', key: 'email' },
      { title: 'Telefon', dataIndex: 'PHONE_NUMBER', key: 'phone' },
      { 
        title: 'Karavan', 
        key: 'caravan',
        render: (user) => `${user.CARAVAN_TYPE || ''} - ${user.CARAVAN_MODEL || ''}`.trim() || 'Belirtilmemiş'
      },
      { title: 'Yıl', dataIndex: 'CARAVAN_YEAR', key: 'year' },
      { 
        title: 'Dil', 
        dataIndex: 'USED_LANGUAGE', 
        key: 'lang',
        render: (lang) => {
          const langs = { 'TR': '🇹🇷 TR', 'EN': '🇬🇧 EN', 'DE': '🇩🇪 DE' };
          return langs[lang] || lang;
        }
      }
    ];

    return (
      <Table
        columns={columns}
        dataSource={cityUsers}
        pagination={{ pageSize: 5 }}
        rowKey="USER_ID"
        size="small"
      />
    );
  };

  // Şehirler için tablo kolonları
  const cityColumns = [
    {
      title: 'Sıra',
      key: 'index',
      width: 60,
      render: (_, __, index) => (
        <Tag color={index < 3 ? 'gold' : 'default'}>
          {index + 1}
        </Tag>
      )
    },
    {
      title: 'Şehir',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <Text strong>{name}</Text>
    },
    {
      title: 'Kullanıcı Sayısı',
      dataIndex: 'count',
      key: 'count',
      sorter: (a, b) => a.count - b.count,
      render: (count) => (
        <Space>
          <TeamOutlined />
          <Text>{count}</Text>
        </Space>
      )
    },
    {
      title: 'Yüzde',
      dataIndex: 'percentage',
      key: 'percentage',
      render: (percentage) => (
        <Progress 
          percent={parseFloat(percentage)} 
          size="small" 
          style={{ minWidth: 80 }}
        />
      )
    },
    {
      title: 'En Çok Karavan',
      dataIndex: 'mostUsedCaravan',
      key: 'mostUsedCaravan',
      render: (type) => (
        <Tag color={
          type === 'motocaravan' ? 'green' : 
          type === 'trailer' ? 'orange' : 
          type === 'camper' ? 'purple' : 'default'
        }>
          {type}
        </Tag>
      )
    },
    {
      title: 'En Çok Dil',
      dataIndex: 'mostUsedLanguage',
      key: 'mostUsedLanguage',
      render: (lang) => {
        const langMap = {
          'TR': { flag: '🇹🇷', name: 'Türkçe' },
          'EN': { flag: '🇬🇧', name: 'İngilizce' },
          'DE': { flag: '🇩🇪', name: 'Almanca' }
        };
        const langInfo = langMap[lang] || { flag: '', name: lang };
        return <Text>{langInfo.flag} {langInfo.name}</Text>;
      }
    }
  ];

  return (
    <div style={{ padding: '24px', backgroundColor: '#f0f2f5', minHeight: '100vh' }}>
      <Spin spinning={loading}>
        {/* Header */}
        <Card bordered={false} style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <Title level={2} style={{ margin: 0 }}>
              <PieChartOutlined style={{ marginRight: '8px' }} />
              İstatistikler
            </Title>
            <Space wrap>
              <div style={{ display: 'flex', gap: '20px', alignItems: 'flex-end' }}>
    {/* ŞEHİR FİLTRELEME DROPDOWNU */}
    <div>
        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Şehir Filtreleme</div>
        <Select
  placeholder="Şehir Seçin"
  value={selectedCity}
  onChange={setSelectedCity}
  allowClear
  style={{ width: 200 }}
>
  {uniqueCities.map(city => (
    <Option key={normalizeCity(city)} value={city}>
      {city}
    </Option>
  ))}
</Select>
    </div>

    {/* KARAVAN TİPİ DROPDOWNU */}
    <div>
        <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>Karavan Tipi</div>
        <Select
            placeholder="Karavan Tipi"
            style={{ width: 150 }}
            allowClear
            value={selectedCaravanType ?? undefined}
            onChange={setSelectedCaravanType}
        >
            {[...new Set(data.map(item => item.CARAVAN_TYPE).filter(Boolean))].map(type => (
                <Option key={type} value={type}>{type}</Option>
            ))}
        </Select>
    </div>
    <div>
        <Button 
                icon={<ArrowLeftOutlined />} 
                onClick={() => navigate('/dashboard')}
              >
                Dashboard'a Dön
              </Button>
            
    </div>
</div>

            </Space>
          </div>
        </Card>

        {/* Genel İstatistikler */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Toplam Kullanıcı"
                value={statistics.totalUsers}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Farklı Şehir"
                value={statistics.citiesCount}
                prefix={<EnvironmentOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Bu Hafta Yeni Kayıt"
                value={statistics.newUsersThisWeek}
                prefix={<RiseOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Bu Ay Yeni Kayıt"
                value={statistics.newUsersThisMonth}
                prefix={<CalendarOutlined />}
                valueStyle={{ color: '#722ed1' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Ana İstatistik Kartları */}
        <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
          {/* Şehir Dağılımı Tablosu */}
          <Col xs={24} lg={16}>
            <Card 
              title={
                <span>
                  <EnvironmentOutlined style={{ marginRight: '8px' }} />
                  Şehir Bazında Kullanıcı Dağılımı (Detayları görmek için satıra tıklayın)
                </span>
              }
            >
              <Table
  dataSource={topCitiesDetailed}
  columns={cityColumns}
  rowKey="name"
  size="small"
  expandable={{
    expandedRowRender: expandedRowRender,
    rowExpandable: (record) => record.users.length > 0
  }}
  pagination={false}
/>
            </Card>
          </Col>

          {/* Sağ Panel - Özet İstatistikler */}
          <Col xs={24} lg={8}>
            {/* Karavan Tipi Dağılımı */}
            <Card 
              title={
                <span>
                  <CarOutlined style={{ marginRight: '8px' }} />
                  Karavan Tipi Dağılımı
                </span>
              }
              style={{ marginBottom: '16px' }}
            >
              {Object.entries(statistics.caravanTypes).map(([type, count]) => {
                const percentage = ((count / statistics.totalUsers) * 100).toFixed(1);
                return (
                  <div key={type} style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <Text>{type}</Text>
                      <Text strong>{count} ({percentage}%)</Text>
                    </div>
                    <Progress 
                      percent={parseFloat(percentage)} 
                      size="small"
                      strokeColor={{
                        'motocaravan': '#52c41a',
                        'trailer': '#faad14',
                        'camper': '#722ed1'
                      }[type] || '#1890ff'}
                    />
                  </div>
                );
              })}
            </Card>

            {/* Dil Dağılımı */}
            <Card 
              title={
                <span>
                  <GlobalOutlined style={{ marginRight: '8px' }} />
                  Dil Dağılımı
                </span>
              }
            >
              {Object.entries(statistics.languages).map(([lang, count]) => {
                const percentage = ((count / statistics.totalUsers) * 100).toFixed(1);
                const langNames = { 'TR': 'Türkçe', 'EN': 'İngilizce', 'DE': 'Almanca' };
                const langFlags = { 'TR': '🇹🇷', 'EN': '🇬🇧', 'DE': '🇩🇪' };
                return (
                  <div key={lang} style={{ marginBottom: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                      <Text>{langFlags[lang]} {langNames[lang] || lang}</Text>
                      <Text strong>{count} ({percentage}%)</Text>
                    </div>
                    <Progress 
                      percent={parseFloat(percentage)} 
                      size="small"
                      strokeColor={{
                        'TR': '#ff0000',
                        'EN': '#0052cc',
                        'DE': '#ffcc00'
                      }[lang] || '#1890ff'}
                    />
                  </div>
                );
              })}
            </Card>
          </Col>
        </Row>

        {/* Alt İstatistikler */}
        <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
          {/* En Popüler Modeller */}
          <Col xs={24} md={8}>
            <Card
              title={
                <span>
                  <BarChartOutlined style={{ marginRight: '8px' }} />
                  En Popüler Karavan Modelleri
                </span>
              }
            >
              <List
                size="small"
                dataSource={topModels}
                renderItem={([model, count], index) => (
                  <List.Item>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <Text style={{ flex: 1 }}>
                        <Tag color={
                          index === 0 ? 'gold' : 
                          index === 1 ? 'silver' : 
                          index === 2 ? '#cd7f32' : 'default'
                        }>
                          {index + 1}
                        </Tag>
                        <span title={model}>
                          {model.length > 20 ? model.substring(0, 20) + '...' : model}
                        </span>
                      </Text>
                      <Text strong>{count}</Text>
                    </div>
                  </List.Item>
                )}
              />
            </Card>
          </Col>

          {/* Yıl Dağılımı */}
          <Col xs={24} md={8}>
            <Card 
              title={
                <span>
                  <CalendarOutlined style={{ marginRight: '8px' }} />
                  Karavan Yıl Dağılımı
                </span>
              }
            >
              {Object.entries(yearDistribution)
                .sort((a, b) => b[0].localeCompare(a[0]))
                .slice(0, 6)
                .map(([range, count]) => {
                  const percentage = ((count / filteredData.filter(item => item.CARAVAN_YEAR).length) * 100).toFixed(1);
                  return (
                    <div key={range} style={{ marginBottom: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <Text>{range}</Text>
                        <Text strong>{count} ({percentage}%)</Text>
                      </div>
                      <Progress 
                        percent={parseFloat(percentage)} 
                        size="small"
                        strokeColor="#1890ff"
                      />
                    </div>
                  );
                })}
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <Text type="secondary">
                  Ortalama Yıl: <Text strong>{Math.round(statistics.averageCaravanYear)}</Text>
                </Text>
              </div>
            </Card>
          </Col>

          {/* Plaka Dağılımı */}
          <Col xs={24} md={8}>
            <Card
              title={
                <span>
                  <CarOutlined style={{ marginRight: '8px' }} />
                  En Çok Plaka Kodu
                </span>
              }
            >
              <List
                size="small"
                dataSource={topPlates}
                renderItem={([plateCode, count], index) => (
                  <List.Item>
                    <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                      <Space>
                        <Tag color="blue">{plateCode}</Tag>
                        <Text>{getCityByPlateCode(plateCode)}</Text>
                      </Space>
                      <Text strong>{count}</Text>
                    </div>
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>

        {/* Özet Bilgiler */}
        <Card style={{ marginTop: '24px' }}>
          <Title level={4} style={{ marginBottom: '24px', textAlign: 'center' }}>
            Özet Bilgiler
          </Title>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={6}>
              <div style={{ textAlign: 'center', borderRight: '1px solid #f0f0f0', paddingRight: '16px' }}>
                <Text type="secondary">En Çok Kullanılan Karavan Tipi</Text>
                <Title level={4} style={{ margin: '8px 0' }}>
                  {Object.entries(statistics.caravanTypes).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Belirtilmemiş'}
                </Title>
                <Text>
                  <Tag color="green">
                    {Object.entries(statistics.caravanTypes).sort((a, b) => b[1] - a[1])[0]?.[1] || 0} kullanıcı
                  </Tag>
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div style={{ textAlign: 'center', borderRight: '1px solid #f0f0f0', paddingRight: '16px' }}>
                <Text type="secondary">En Yaygın Dil</Text>
                <Title level={4} style={{ margin: '8px 0' }}>
                  {{
                    'TR': '🇹🇷 Türkçe',
                    'EN': '🇬🇧 İngilizce', 
                    'DE': '🇩🇪 Almanca'
                  }[Object.entries(statistics.languages).sort((a, b) => b[1] - a[1])[0]?.[0]] || 'Belirtilmemiş'}
                </Title>
                <Text>
                  <Tag color="blue">
                    {Object.entries(statistics.languages).sort((a, b) => b[1] - a[1])[0]?.[1] || 0} kullanıcı
                  </Tag>
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div style={{ textAlign: 'center', borderRight: '1px solid #f0f0f0', paddingRight: '16px' }}>
                <Text type="secondary">En Yoğun Şehir</Text>
                <Title level={4} style={{ margin: '8px 0' }}>
                  {topCities[0]?.name || 'Belirtilmemiş'}
                </Title>
                <Text>
                  <Tag color="volcano">
                    {topCities[0]?.count || 0} kullanıcı
                  </Tag>
                </Text>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary">En Popüler Model</Text>
                <Title level={4} style={{ margin: '8px 0' }}>
                  {topModels[0]?.[0]?.substring(0, 20) || 'Belirtilmemiş'}
                </Title>
                <Text>
                  <Tag color="purple">
                    {topModels[0]?.[1] || 0} kullanıcı
                  </Tag>
                </Text>
              </div>
            </Col>
          </Row>
        </Card>
      </Spin>
    </div>
  );
};

// Plaka kodlarına göre şehir isimleri
const getCityByPlateCode = (code) => {
  const plateCodes = {
    '01': 'Adana', '02': 'Adıyaman', '03': 'Afyon', '04': 'Ağrı',
    '05': 'Amasya', '06': 'Ankara', '07': 'Antalya', '08': 'Artvin',
    '09': 'Aydın', '10': 'Balıkesir', '11': 'Bilecik', '12': 'Bingöl',
    '13': 'Bitlis', '14': 'Bolu', '15': 'Burdur', '16': 'Bursa',
    '17': 'Çanakkale', '18': 'Çankırı', '19': 'Çorum', '20': 'Denizli',
    '21': 'Diyarbakır', '22': 'Edirne', '23': 'Elazığ', '24': 'Erzincan',
    '25': 'Erzurum', '26': 'Eskişehir', '27': 'Gaziantep', '28': 'Giresun',
    '29': 'Gümüşhane', '30': 'Hakkari', '31': 'Hatay', '32': 'Isparta',
    '33': 'İçel', '34': 'İstanbul', '35': 'İzmir', '36': 'Kars',
    '37': 'Kastamonu', '38': 'Kayseri', '39': 'Kırklareli', '40': 'Kırşehir',
    '41': 'Kocaeli', '42': 'Konya', '43': 'Kütahya', '44': 'Malatya',
    '45': 'Manisa', '46': 'Kahramanmaraş', '47': 'Mardin', '48': 'Muğla',
    '49': 'Muş', '50': 'Nevşehir', '51': 'Niğde', '52': 'Ordu',
    '53': 'Rize', '54': 'Sakarya', '55': 'Samsun', '56': 'Siirt',
    '57': 'Sinop', '58': 'Sivas', '59': 'Tekirdağ', '60': 'Tokat',
    '61': 'Trabzon', '62': 'Tunceli', '63': 'Şanlıurfa', '64': 'Uşak',
    '65': 'Van', '66': 'Yozgat', '67': 'Zonguldak', '68': 'Aksaray',
    '69': 'Bayburt', '70': 'Karaman', '71': 'Kırıkkale', '72': 'Batman',
    '73': 'Şırnak', '74': 'Bartın', '75': 'Ardahan', '76': 'Iğdır',
    '77': 'Yalova', '78': 'Karabük', '79': 'Kilis', '80': 'Osmaniye',
    '81': 'Düzce'
  };
  return plateCodes[code] || code;
};

export default Total;